import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Play, Power } from "lucide-react";
import { useState } from "react";
import { api, type ImportJob } from "@/lib/api";
import { ErrorState } from "@/components/empty-state";
import { ShopLogo } from "@/components/shop-logo";

export const Route = createFileRoute("/admin/cjs-cdkeys")({
  component: AdminCjs,
});

const KEY = ["admin-cjs-status"];

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-lg font-bold">{value}</div>
    </div>
  );
}

function AdminCjs() {
  const qc = useQueryClient();
  const [limit, setLimit] = useState("");
  const status = useQuery({ queryKey: KEY, queryFn: () => api.adminCjsStatus(), refetchInterval: 4000 });
  const run = useMutation({
    mutationFn: (o: { dryRun?: boolean }) => api.adminImportCjs({ ...o, limit: limit ? Number(limit) : undefined }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; isActive: boolean }) => api.adminUpdateShop(v.id, { isActive: v.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const data = status.data?.data;
  const jobs = data?.jobs ?? [];
  const latest = jobs[0];
  const running = jobs.some((j) => j.status === "running");
  const shop = data?.shop;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">CJS CD Keys import</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Hourly CSV feed with EUR, GBP and USD prices. Every offer links through AWIN. Accounts,
            gift cards and subscriptions are never imported.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="Limit (optional)"
            className="w-40 rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
          <button
            onClick={() => run.mutate({ dryRun: true })}
            disabled={running || run.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold disabled:opacity-50"
          >
            <FlaskConical className="h-3.5 w-3.5" /> Dry run
          </button>
          <button
            onClick={() => run.mutate({})}
            disabled={running || run.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Play className="h-3.5 w-3.5" /> {running ? "Running…" : "Sync now"}
          </button>
        </div>
      </header>

      {run.isError && <ErrorState message={(run.error as Error).message} />}
      {toggle.isError && <ErrorState message={(toggle.error as Error).message} />}
      {status.isError && <ErrorState message={(status.error as Error).message} />}

      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4 text-sm">
        <ShopLogo shop={shop ?? { name: "CJS CD Keys", slug: "cjs-cdkeys", logoUrl: null }} size="lg" />
        <div>
          <div className="font-semibold">{shop?.name ?? "CJS CD Keys — created on first sync"}</div>
          <div className="text-xs text-muted-foreground">
            Network: {data?.network ?? "AWIN"} · Advertiser {data?.awin.advertiserId ?? "30147"} · Publisher{" "}
            {data?.awin.publisherId ?? "3096737"}
          </div>
        </div>
        {shop && (
          <button
            onClick={() => toggle.mutate({ id: shop.id, isActive: !shop.isActive })}
            disabled={toggle.isPending}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold hover:border-primary disabled:opacity-50"
          >
            <Power className="h-3.5 w-3.5" /> {shop.isActive ? "Enabled — disable" : "Disabled — enable"}
          </button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Import status" value={latest ? latest.status : "never run"} />
        <Stat label="Total offers" value={(data?.totalOffers ?? 0).toLocaleString()} />
        <Stat label="Matched (last run)" value={(latest?.matchedItems ?? 0).toLocaleString()} />
        <Stat label="Unmatched (last run)" value={(latest?.unmatchedItems ?? 0).toLocaleString()} />
        <Stat
          label="Last successful sync"
          value={data?.lastSuccess?.finishedAt ? new Date(data.lastSuccess.finishedAt).toLocaleString() : "—"}
        />
        {(data?.offerCounts ?? []).map((c) => (
          <Stat key={c.currency} label={`Active ${c.currency}`} value={c.count.toLocaleString()} />
        ))}
      </div>

      {latest?.status === "failed" && latest.errorMessage && <ErrorState message={latest.errorMessage} />}
      {(data?.recentErrors ?? []).length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-2 text-sm font-semibold">Error log (latest run)</h2>
          <ul className="space-y-1 font-mono text-xs text-muted-foreground">
            {data!.recentErrors.map((e) => (
              <li key={e.id}>{new Date(e.createdAt).toLocaleTimeString()} — {e.errorMessage}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Started</th>
              <th className="px-4 py-3 text-right">Matched</th>
              <th className="px-4 py-3 text-right">Unmatched</th>
              <th className="px-4 py-3 text-right">Failed</th>
              <th className="px-4 py-3 text-right">Imported</th>
              <th className="px-4 py-3 text-right">Rows</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-3 text-muted-foreground">No imports yet.</td></tr>
            )}
            {jobs.map((j: ImportJob) => (
              <tr key={j.id} className="border-t border-border/60" title={j.errorMessage ?? undefined}>
                <td className="px-4 py-2">{j.source}</td>
                <td className={`px-4 py-2 ${j.status === "completed" ? "text-success" : j.status === "failed" ? "text-destructive" : "text-primary"}`}>{j.status}</td>
                <td className="px-4 py-2 text-muted-foreground">{new Date(j.startedAt).toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{j.matchedItems ?? 0}</td>
                <td className="px-4 py-2 text-right">{j.unmatchedItems ?? 0}</td>
                <td className="px-4 py-2 text-right">{j.failedItems ?? 0}</td>
                <td className="px-4 py-2 text-right">{j.importedItems ?? 0}</td>
                <td className="px-4 py-2 text-right">{j.totalItems ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
